import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./qimen-dunjia-enter-style.css";
import useIsMobile from "../../../../hooks/useIsMobile";
import SiteFooter from "../../../custom/site-footer/site-footer";
import FunctionService from "../../../../service/function-service";

//奇門遁甲 — enter 頁（介紹頁）
//  介紹 + 痛點 + 影片 + 事件預覽 + CTA
//  點 CTA / 事件卡 → 跳 /qimen-dunjia/pass（需要時帶 presetEvent 預選）
const QimenDunjiaEnterPage = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [navHeight, setNavHeight] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [events, setEvents] = useState([]); //從 API 拉的事件清單

  useEffect(() => {
    const nav = document.querySelector(
      isMobile ? ".mobile-dashboard nav" : ".dashboard nav",
    );
    setNavHeight(nav ? nav.offsetHeight : 0);
  }, [isMobile]);

  //拉 meta：F-6 把 events 內嵌在 categories 底下，flatMap 攤平給這頁用
  useEffect(() => {
    FunctionService.getQimenMeta()
      .then((res) => {
        const cats = res.data.data?.categories || [];
        setEvents(cats.flatMap((c) => c.events || []));
      })
      .catch((e) => console.error("[QimenEnter] 拉 meta 失敗:", e));
  }, []);

  //進 pass 頁：可選帶 presetEvent 直接從 Step 2 開始
  const goToPass = (presetEvent = null) => {
    navigate("/qimen-dunjia/pass", {
      state: presetEvent ? { presetEvent } : undefined,
    });
  };

  //banner 點擊 → 滾到 fifth-block（三步驟 + 立即查詢吉時 CTA）
  const ctaSectionRef = useRef(null);
  const scrollToCta = () => {
    ctaSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const Content = () => (
    <>
      <img
        className="banner"
        src={
          isMobile
            ? `/images/bg-qimen-mobile-banner.png`
            : `/images/bg-qimen-web-banner.png`
        }
        loading="lazy"
        alt="奇門遁甲"
        onClick={scrollToCta}
      />

      <div className="first-block">
        <p className="slogan">
          很多時候，不是運不夠好
          <br />
          而是「沒選對時間、沒選對方向」
        </p>

        <div className="feeling-question">
          <img src={`/images/ic-question-mark.png`} alt="問號" />
          <p className="question">你是不是也有這種感覺：</p>
          <p className="feeling">合約要簽，但哪一天簽才順？🤝</p>
          <p className="feeling">想搬家，可是吉日怎麼挑？🏠</p>
          <p className="feeling">面試在即，幾點出門最有利？📚</p>
        </div>
      </div>

      <div className="second-block">
        <p>
          奇門遁甲是古代帝王將相用來決策的「時空導航」
          <br />
          告訴你 — 在什麼時間、面向什麼方向 、做對應的事，才能順勢而為
        </p>
      </div>

      {/* 介紹影片 — 暫時隱藏，影片素材備妥後再打開 */}
      {/* <div className="third-block">
        <img
          className="trigrams-icon"
          src={`/images/ic-trigrams.png`}
          alt="奇門遁甲八卦"
          loading="lazy"
        />
        <p className="video-title">3 分鐘看懂奇門遁甲</p>
        <button className="video-thumb" onClick={() => setShowVideo(true)}>
          <img
            src={`/images/bg-flow-web-banner.png`}
            alt="奇門遁甲介紹影片"
            loading="lazy"
          />
          <div className="play-overlay">
            <i className="bi bi-play-fill"></i>
          </div>
        </button>
      </div> */}

      {/* 時盤 vs 日盤：解釋兩種盤的差異 + 適用情境 */}
      <div className="fourth-block">
        <p className="plates-title">兩種盤，依你的情境選</p>
        <p className="plates-sub">系統會根據事件自動推薦，你也可以自己挑</p>
        <div className="plates-grid">
          <div className="plate-intro-card time">
            <div className="icon-wrap">
              <i className="bi bi-clock-fill"></i>
            </div>
            <h3>時盤</h3>
            <p className="desc">
              選定<strong>某一天</strong>，找出當天的<strong>最佳時辰</strong>
            </p>
            <div className="example">
              <span className="example-label">例</span>
              <span>今天要簽合約，幾點動最順？</span>
            </div>
          </div>
          <div className="plate-intro-card day">
            <div className="icon-wrap">
              <i className="bi bi-calendar2-week-fill"></i>
            </div>
            <h3>日盤</h3>
            <p className="desc">
              選定<strong>日期區間</strong>，找出區間內的
              <strong>最佳吉日</strong>
            </p>
            <div className="example">
              <span className="example-label">例</span>
              <span>下個月想搬家，哪一天最好？</span>
            </div>
          </div>
        </div>
      </div>

      {/* 三步驟說明 + CTA（提早放，讓用戶更早看到立即查詢按鈕） */}
      <div className="fifth-block" ref={ctaSectionRef}>
        <p className="title">三步驟，找出你的吉時吉位</p>
        <div className="features">
          <div className="feature-item">
            <div className="num">1</div>
            <div className="text">
              <p className="t">選你要做的事</p>
              <p className="d">六大類別、30+ 種情境</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="num">2</div>
            <div className="text">
              <p className="t">選盤別</p>
              <p className="d">系統推薦最適合的盤</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="num">3</div>
            <div className="text">
              <p className="t">看吉時與方位</p>
              <p className="d">最佳行動時機一目了然</p>
            </div>
          </div>
        </div>

        <button className="cta-btn" onClick={() => goToPass()}>
          <i className="bi bi-stars"></i> 立即查詢吉時
        </button>
      </div>

      {/* 事件預覽：只顯示後台標記「常用」的（isFeatured=true） */}
      <div className="six-block">
        <p className="preview-title">常見使用情境</p>
        <p className="preview-sub"></p>
        <div className="preview-grid">
          {events
            .filter((ev) => ev.isFeatured)
            .map((ev) => (
              <div key={ev.eventId} className="preview-card">
                <div className="icon-wrap">
                  <i className={`bi ${ev.icon}`}></i>
                </div>
                <span className="event-label">{ev.label}</span>
              </div>
            ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="qimen-dunjia-page">
      {!isMobile ? (
        <main
          className="web-main"
          style={{ paddingTop: navHeight }}
          onMouseEnter={() => {
            setShowServiceMenu?.(false);
            setShowPersonalMenu?.(false);
          }}
        >
          <Content />
        </main>
      ) : (
        <main className="mobile-main" style={{ paddingTop: navHeight }}>
          <Content />
        </main>
      )}

      <SiteFooter />

      {showVideo && (
        <div className="video-modal" onClick={() => setShowVideo(false)}>
          <div
            className="video-modal-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="video-close" onClick={() => setShowVideo(false)}>
              <i className="bi bi-x"></i>
            </button>
            <div className="video-frame">
              <p className="video-placeholder">
                <i className="bi bi-camera-video-fill"></i>
                <br />
                介紹影片素材待補
                <br />
                <span className="hint">
                  把 iframe 的 src 換成 YouTube / Vimeo embed URL
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QimenDunjiaEnterPage;
